<script setup lang="ts">
import { config } from '@/config'
const enabled = config.enableDevConfigurator === 'true'
const visible = ref(false)

const agentName = ref('')
const baseUrl = ref('')
const internalToken = ref('')

const macOS = computed(
  () =>
    typeof navigator !== 'undefined' &&
    !!navigator.userAgent &&
    navigator.userAgent.includes('Macintosh;'),
)

const ls = {
  get: (k: string) => {
    try {
      return localStorage.getItem(k)
    } catch {
      return null
    }
  },
  set: (k: string, v: string) => {
    try {
      localStorage.setItem(k, v)
    } catch {
      /* intentional */
    }
  },
  del: (k: string) => {
    try {
      localStorage.removeItem(k)
    } catch {
      /* intentional */
    }
  },
}

const K = {
  agentName: 'devcfg:agentName',
  baseUrl: 'devcfg:baseUrl',
  internalToken: 'devcfg:internalToken',
} as const

// Toggle clavier : Shift + ⌘/Ctrl + D
function toggleDevPanel(event: KeyboardEvent) {
  const ctrlOrCmd = macOS.value ? event.metaKey : event.ctrlKey
  if (event.shiftKey && ctrlOrCmd && event.key.toLowerCase() === 'd') {
    event.preventDefault()
    visible.value = !visible.value
  }
}

onMounted(() => {
  if (!enabled) return

  const params = new URLSearchParams(window.location.search)
  const qAgent = params.get('agentName')
  const qBase = params.get('baseUrl')
  const qToken = params.get('internalToken')

  agentName.value = qAgent ?? ls.get(K.agentName) ?? ''
  baseUrl.value = qBase ?? ls.get(K.baseUrl) ?? ''
  internalToken.value = qToken ?? ls.get(K.internalToken) ?? ''

  window.addEventListener('keydown', toggleDevPanel)
})

onBeforeUnmount(() => {
  if (!enabled) return
  window.removeEventListener('keydown', toggleDevPanel)
})

watch([agentName, baseUrl, internalToken], ([name, base, token]) => {
  const url = new URL(window.location.href)

  if (name) url.searchParams.set('agentName', name)
  else url.searchParams.delete('agentName')

  if (base) url.searchParams.set('baseUrl', base)
  else url.searchParams.delete('baseUrl')

  if (token) url.searchParams.set('internalToken', token)
  else url.searchParams.delete('internalToken')

  window.history.replaceState({}, '', url.toString())

  if (name) ls.set(K.agentName, name)
  else ls.del(K.agentName)
  if (base) ls.set(K.baseUrl, base)
  else ls.del(K.baseUrl)
  if (token) ls.set(K.internalToken, token)
  else ls.del(K.internalToken)
})
</script>

<template>
  <div
    v-if="enabled && visible"
    class="fixed bottom-4 left-4 p-4 w-[360px] bg-white border border-gray-300 rounded-md shadow-lg z-50 space-y-3 text-sm"
  >
    <h3 class="text-base font-semibold text-gray-700">Dev Configurator</h3>

    <OInput v-model="agentName" label="Agent Name" />
    <OInput v-model="baseUrl" label="Base URL" />
    <OInput v-model="internalToken" label="Internal Token" />

    <p class="text-gray-400 text-xs">
      Press <kbd>Shift</kbd> + <kbd>{{ macOS ? '⌘' : 'Ctrl' }}</kbd> + <kbd>D</kbd> to toggle
    </p>
  </div>
</template>
